import type { IconFontColor } from '@/shared/types/types';
import type {
  GhanttBarLabelStage,
  GhanttBarLabelTheme,
  GhanttBarLabelType,
} from '../../types/ghanttBarLabel';

export interface GhanttBarLabelThemeTokens {
  border: string;
  surface: string;
  progressFill: string;
  /** Fondo suave + borde semántico en estado hover (estático o `:hover`). */
  hoverSurface: string;
  hoverBorder: string;
  hoverCardStatic: string;
  hoverCardInteractive: string;
  title: string;
  meta: string;
  titleHover: string;
  metaHover: string;
  icon: IconFontColor;
  showStatusIcon: boolean;
}

const THEME_TOKENS: Record<GhanttBarLabelTheme, GhanttBarLabelThemeTokens> = {
  default: {
    border: 'ui-border-default',
    surface: 'ui-bg-surface-default',
    progressFill: 'ui-bg-surface-brand-default',
    hoverSurface: 'ui-bg-surface-brand-default',
    hoverBorder: 'ui-border-brand',
    hoverCardStatic:
      'ui-bg-surface-brand-default ui-border-brand ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-brand-default hover:ui-border-brand hover:ui-shadow-03',
    title: 'ui-text-brand',
    meta: 'ui-text-brand',
    titleHover: 'group-hover:ui-text-brand-hover',
    metaHover: 'group-hover:ui-text-brand-hover',
    icon: 'brand',
    showStatusIcon: false,
  },
  complete: {
    border: 'ui-border-success',
    surface: 'ui-bg-surface-default',
    progressFill: 'ui-bg-surface-success-default',
    hoverSurface: 'ui-bg-surface-success-default',
    hoverBorder: 'ui-border-success',
    hoverCardStatic:
      'ui-bg-surface-success-default ui-border-success ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-success-default hover:ui-border-success hover:ui-shadow-03',
    title: 'ui-text-success',
    meta: 'ui-text-success',
    titleHover: 'group-hover:ui-text-success-hover',
    metaHover: 'group-hover:ui-text-success-hover',
    icon: 'success',
    showStatusIcon: true,
  },
  delayed: {
    border: 'ui-border-warning',
    surface: 'ui-bg-surface-default',
    progressFill: 'ui-bg-surface-warning-default',
    hoverSurface: 'ui-bg-surface-warning-default',
    hoverBorder: 'ui-border-warning',
    hoverCardStatic:
      'ui-bg-surface-warning-default ui-border-warning ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-warning-default hover:ui-border-warning hover:ui-shadow-03',
    title: 'ui-text-warning',
    meta: 'ui-text-warning',
    titleHover: 'group-hover:ui-text-warning-hover',
    metaHover: 'group-hover:ui-text-warning-hover',
    icon: 'warning',
    showStatusIcon: true,
  },
  danger: {
    border: 'ui-border-danger',
    surface: 'ui-bg-surface-default',
    progressFill: 'ui-bg-surface-danger-default',
    hoverSurface: 'ui-bg-surface-danger-default',
    hoverBorder: 'ui-border-danger',
    hoverCardStatic:
      'ui-bg-surface-danger-default ui-border-danger ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-danger-default hover:ui-border-danger hover:ui-shadow-03',
    title: 'ui-text-danger',
    meta: 'ui-text-danger',
    titleHover: 'group-hover:ui-text-danger-hover',
    metaHover: 'group-hover:ui-text-danger-hover',
    icon: 'danger',
    showStatusIcon: true,
  },
};

const TIMELINE_THEME_TOKENS: Record<GhanttBarLabelTheme, GhanttBarLabelThemeTokens> = {
  default: {
    border: 'ui-border-default',
    surface: 'ui-bg-fill-default',
    progressFill: 'ui-bg-surface-secondary-default',
    hoverSurface: 'ui-bg-surface-brand-default',
    hoverBorder: 'ui-border-brand',
    hoverCardStatic:
      'ui-bg-surface-brand-default ui-border-brand ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-brand-default hover:ui-border-brand hover:ui-shadow-03',
    title: 'ui-text-primary',
    meta: 'ui-text-tertiary',
    titleHover: 'group-hover:ui-text-brand-hover',
    metaHover: 'group-hover:ui-text-brand-hover',
    icon: 'secondary',
    showStatusIcon: false,
  },
  complete: {
    border: 'ui-border-success',
    surface: 'ui-bg-fill-default',
    progressFill: 'ui-bg-surface-success-default',
    hoverSurface: 'ui-bg-surface-success-default',
    hoverBorder: 'ui-border-success',
    hoverCardStatic:
      'ui-bg-surface-success-default ui-border-success ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-success-default hover:ui-border-success hover:ui-shadow-03',
    title: 'ui-text-success',
    meta: 'ui-text-success',
    titleHover: 'group-hover:ui-text-success-hover',
    metaHover: 'group-hover:ui-text-success-hover',
    icon: 'success',
    showStatusIcon: true,
  },
  delayed: {
    border: 'ui-border-warning',
    surface: 'ui-bg-fill-default',
    progressFill: 'ui-bg-surface-warning-default',
    hoverSurface: 'ui-bg-surface-warning-default',
    hoverBorder: 'ui-border-warning',
    hoverCardStatic:
      'ui-bg-surface-warning-default ui-border-warning ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-warning-default hover:ui-border-warning hover:ui-shadow-03',
    title: 'ui-text-warning',
    meta: 'ui-text-warning',
    titleHover: 'group-hover:ui-text-warning-hover',
    metaHover: 'group-hover:ui-text-warning-hover',
    icon: 'warning',
    showStatusIcon: true,
  },
  danger: {
    border: 'ui-border-danger',
    surface: 'ui-bg-fill-default',
    progressFill: 'ui-bg-surface-danger-default',
    hoverSurface: 'ui-bg-surface-danger-default',
    hoverBorder: 'ui-border-danger',
    hoverCardStatic:
      'ui-bg-surface-danger-default ui-border-danger ui-shadow-03',
    hoverCardInteractive:
      'hover:ui-bg-surface-danger-default hover:ui-border-danger hover:ui-shadow-03',
    title: 'ui-text-danger',
    meta: 'ui-text-danger',
    titleHover: 'group-hover:ui-text-danger-hover',
    metaHover: 'group-hover:ui-text-danger-hover',
    icon: 'danger',
    showStatusIcon: true,
  },
};

const STATIC_SELECT_CARD = ['ui-border-focus', 'ui-shadow-03'];
const STATIC_DRAGGING_CARD = ['ui-border-focus', 'ui-shadow-03', 'ui-border-dashed'];

/** Select persiste con borde focus; el hover semántico no aplica si `selected`. */
export const GHANTT_BAR_LABEL_SELECTED_CARD =
  'data-[selected=true]:ui-border-focus data-[selected=true]:ui-shadow-03 data-[selected=true]:ui-bg-surface-brand-pressed';

export const GHANTT_BAR_LABEL_INTERACTIVE_FOCUS =
  'focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-border-brand';

/** Transición suave en hover, select y cambios de superficie. */
export const GHANTT_BAR_LABEL_TRANSITION_CARD =
  'ui-transition-[background-color,border-color,box-shadow] ui-duration-150 ui-ease-in-out motion-reduce:ui-transition-none';

export const GHANTT_BAR_LABEL_TRANSITION_TEXT =
  'ui-transition-colors ui-duration-150 ui-ease-in-out motion-reduce:ui-transition-none';

export const GHANTT_BAR_LABEL_TRANSITION_PROGRESS =
  'ui-transition-[width,background-color] ui-duration-150 ui-ease-in-out motion-reduce:ui-transition-none';

export function ghanttBarLabelResolveTheme(
  type: GhanttBarLabelType,
  theme: GhanttBarLabelTheme
): GhanttBarLabelThemeTokens {
  return type === 'timeline' ? TIMELINE_THEME_TOKENS[theme] : THEME_TOKENS[theme];
}

export function ghanttBarLabelCardClasses(
  stage: GhanttBarLabelStage,
  themeTokens: GhanttBarLabelThemeTokens,
  type: GhanttBarLabelType,
  options?: { interactive?: boolean; selected?: boolean }
): string {
  const interactive = options?.interactive === true;
  const selected = options?.selected === true;

  const base = [
    'ui-group ui-relative ui-box-border ui-overflow-hidden ui-rounded-control-md ui-border-1',
    GHANTT_BAR_LABEL_TRANSITION_CARD,
    themeTokens.surface,
    themeTokens.border,
  ];

  if (stage === 'hover') {
    base.push(themeTokens.hoverCardStatic);
  } else if (stage === 'select') {
    base.push(...STATIC_SELECT_CARD);
  } else if (stage === 'dragging') {
    base.push(...STATIC_DRAGGING_CARD);
  }

  if (stage === 'disabled') {
    base.push('ui-border-disabled');
  }

  if (stage === 'select' && type === 'progress') {
    base.push('ui-bg-surface-brand-pressed');
  }

  if (interactive && stage === 'default') {
    base.push(
      themeTokens.hoverCardInteractive,
      GHANTT_BAR_LABEL_SELECTED_CARD,
      GHANTT_BAR_LABEL_INTERACTIVE_FOCUS,
      'ui-cursor-pointer ui-text-left',
      'data-[selected=true]:hover:ui-bg-surface-brand-pressed data-[selected=true]:hover:ui-border-focus'
    );
    if (selected && type === 'progress') {
      base.push('ui-bg-surface-brand-pressed');
    }
  }

  return base.join(' ');
}

export function ghanttBarLabelTextClasses(
  stage: GhanttBarLabelStage,
  themeTokens: GhanttBarLabelThemeTokens,
  options?: { interactive?: boolean; selected?: boolean; type?: GhanttBarLabelType }
): { title: string; meta: string } {
  const interactive = options?.interactive === true;
  const selected = options?.selected === true;
  const type = options?.type ?? 'progress';
  const selectedProgress = (stage === 'select' || (interactive && selected)) && type === 'progress';

  if (stage === 'disabled') {
    return { title: 'ui-text-disabled', meta: 'ui-text-disabled' };
  }

  if (selectedProgress) {
    return { title: 'ui-text-on-color', meta: 'ui-text-on-color' };
  }

  const title = [themeTokens.title];
  const meta = [themeTokens.meta];

  if (stage === 'hover') {
    title.push(themeTokens.titleHover.replace('group-hover:', ''));
    meta.push(themeTokens.metaHover.replace('group-hover:', ''));
  } else if (interactive && stage === 'default') {
    title.push(themeTokens.titleHover);
    meta.push(themeTokens.metaHover);
  }

  return { title: title.join(' '), meta: meta.join(' ') };
}

export function ghanttBarLabelProgressFillClass(
  stage: GhanttBarLabelStage,
  themeTokens: GhanttBarLabelThemeTokens,
  type: GhanttBarLabelType,
  options?: { interactive?: boolean; selected?: boolean }
): string {
  if (type !== 'progress') {
    return themeTokens.progressFill;
  }

  if (stage === 'select' || (options?.interactive && options?.selected)) {
    return 'ui-bg-fill-brand';
  }

  return themeTokens.progressFill;
}
